import * as Sentry from "@sentry/react";

// Get the Sentry logger
export const { logger } = Sentry;

/**
 * Wrapper for API calls with Sentry performance monitoring
 * @param endpoint - The API endpoint being called
 * @param fetcher - The async function that performs the API call
 * @returns The result of the API call
 */
export async function trackApiCall<T>(
  endpoint: string,
  fetcher: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      op: "http.client",
      name: endpoint,
    },
    async () => {
      try {
        const result = await fetcher();
        return result;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    }
  );
}

/**
 * Track UI interactions with Sentry
 * @param action - The name of the action (e.g., "Save Expense")
 * @param handler - The function to execute
 * @param attributes - Optional attributes to add to the span
 */
export function trackUIAction(
  action: string,
  handler: () => void,
  attributes?: Record<string, string | number | boolean>
): void {
  Sentry.startSpan(
    {
      op: "ui.click",
      name: action,
    },
    (span: Sentry.Span) => {
      // Add custom attributes if provided
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }

      try {
        handler();
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    }
  );
}

/**
 * Track async UI actions with Sentry
 * @param action - The name of the action
 * @param handler - The async function to execute
 * @param attributes - Optional attributes to add to the span
 */
export async function trackAsyncUIAction<T>(
  action: string,
  handler: () => Promise<T>,
  attributes?: Record<string, string | number | boolean>
): Promise<T> {
  return Sentry.startSpan(
    {
      op: "ui.action",
      name: action,
    },
    async (span: Sentry.Span) => {
      // Add custom attributes if provided
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }

      try {
        const result = await handler();
        return result;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    }
  );
}

/**
 * Log and capture errors with context
 * @param error - The error to capture
 * @param context - Additional context for the error
 */
export function captureErrorWithContext(
  error: Error,
  context: Record<string, any>
): void {
  logger.error(error.message, context);
  Sentry.captureException(error, {
    contexts: {
      custom: context,
    },
  });
}

/**
 * Track database operations
 * @param operation - The database operation name
 * @param handler - The async function performing the database operation
 */
export async function trackDatabaseOperation<T>(
  operation: string,
  handler: () => Promise<T>
): Promise<T> {
  return Sentry.startSpan(
    {
      op: "db.query",
      name: operation,
    },
    async () => {
      try {
        const startTime = performance.now();
        const result = await handler();
        const duration = performance.now() - startTime;

        logger.debug(
          logger.fmt`Database operation ${operation} completed in ${duration}ms`
        );

        return result;
      } catch (error) {
        logger.error(logger.fmt`Database operation ${operation} failed`, {
          error,
        });
        Sentry.captureException(error);
        throw error;
      }
    }
  );
}
